/**
 * Newsletter signup — posts to the Fugue Lab App's public subscribe endpoint,
 * which upserts the contact into the app's contact kernel and enrolls the
 * subscriber in the welcome sequence. Studio owns no subscriber data; the app
 * (fugue-lab-{env}-data) is the single store.
 *
 * Endpoint: POST {APP_URL}/api/public/o/{slug}/subscribe  body: { email, name? }
 * (same app public API the intake form already calls; CORS already allows the
 * studio domain via the API Gateway `studio_domain` origin.)
 */
import { APP_URL } from './public-trpc'

/**
 * Slug of the Fugue Lab marketing org that owns the list. The org MUST exist in
 * the app (created in fugue-lab-{env}-data) for signups to land; until then the
 * endpoint returns 404. Override per environment via VITE_FUGUE_ORG_SLUG.
 * Default matches the org created by app/scripts/create-org.ts (slug 'fuguelab').
 */
export const FUGUE_ORG_SLUG = import.meta.env.VITE_FUGUE_ORG_SLUG || 'fuguelab'

export interface NewsletterSubscribeResult {
  success?: boolean
  message?: string
  alreadySubscribed?: boolean
}

export async function subscribeToNewsletter(input: {
  email: string
  name?: string
}): Promise<NewsletterSubscribeResult> {
  const res = await fetch(`${APP_URL}/api/public/o/${FUGUE_ORG_SLUG}/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: input.email.trim(), name: input.name?.trim() || undefined }),
  })

  let data: NewsletterSubscribeResult & { error?: string } = {}
  try {
    data = await res.json()
  } catch {
    // empty/non-JSON body
  }

  if (!res.ok) {
    throw new Error(data.error || 'Something went wrong. Please try again.')
  }
  return data
}
